if (!customElements.get('product-measurement-calculator')) {
  customElements.define(
    'product-measurement-calculator',
    class ProductMeasurementCalculator extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode || 'm2';
        this.behaviour = this.dataset.behaviour || 'product';
        this.productFormId = this.dataset.productFormId;

        this.lengthInput = this.querySelector('[data-measurement-length]');
        this.widthInput = this.querySelector('[data-measurement-width]');
        this.depthInput = this.querySelector('[data-measurement-depth]');

        this.result = this.querySelector('[data-measurement-result]');
        this.resultMain = this.querySelector('[data-result-main]');
        this.resultOrder = this.querySelector('[data-result-order]');
        this.error = this.querySelector('[data-measurement-error]');

        this.variants = this.getVariants();
        this.m3Variants = this.findM3Variants();

        this.form = null;
        this.submitButton = null;
        this.state = null;

        this.boundSubmitHandler = this.handleProductSubmit.bind(this);
        this.boundClickHandler = this.handleButtonClick.bind(this);

        this.querySelectorAll('input').forEach((input) => {
          input.addEventListener('input', () => {
            this.calculate();
            this.attachCartHandlers();
          });
        });

        if (this.behaviour === 'product') {
          this.attachCartHandlers();
          requestAnimationFrame(() => this.attachCartHandlers());
          setTimeout(() => this.attachCartHandlers(), 250);
          setTimeout(() => this.attachCartHandlers(), 750);
        }

        this.calculate();

        console.info(
          `[Measurement calculator] Loaded robust cart handler for ${this.mode.toUpperCase()}`
        );
      }

      attachCartHandlers() {
        if (this.behaviour !== 'product') return;

        this.form = this.findProductForm();
        this.submitButton = this.getSubmitButton();

        if (this.form && !this.form.dataset.measurementCalculatorSubmitBound) {
          this.form.addEventListener('submit', this.boundSubmitHandler, true);
          this.form.dataset.measurementCalculatorSubmitBound = 'true';
        }

        if (this.submitButton && !this.submitButton.dataset.measurementCalculatorClickBound) {
          this.submitButton.addEventListener('click', this.boundClickHandler, true);
          this.submitButton.dataset.measurementCalculatorClickBound = 'true';
        }
      }

      findProductForm() {
        if (this.productFormId) {
          const formById = document.getElementById(this.productFormId);
          if (formById) return formById;
        }

        return (
          document.querySelector('form[data-type="add-to-cart-form"]') ||
          document.querySelector('form[action*="/cart/add"]')
        );
      }

      getSubmitButton() {
        const form = this.form || this.findProductForm();

        if (!form) return null;

        return (
          form.querySelector('[type="submit"][name="add"]') ||
          form.querySelector('[type="submit"]')
        );
      }

      getRoutes() {
        const globalRoutes =
          window.routes ||
          (typeof routes !== 'undefined' ? routes : {}) ||
          {};

        return {
          cartAddUrl: globalRoutes.cart_add_url || '/cart/add',
          cartUrl: globalRoutes.cart_url || '/cart',
        };
      }

      getMiniCart() {
        return document.querySelector('mini-cart') || window.miniCart || null;
      }

      getVariants() {
        try {
          return JSON.parse(this.dataset.variants || '[]');
        } catch (error) {
          console.warn('[Measurement calculator] Could not parse variant data.', error);
          return [];
        }
      }

      normalise(value) {
        return String(value || '')
          .toLowerCase()
          .replaceAll('&sup3;', '3')
          .replaceAll('&#179;', '3')
          .replaceAll('³', '3')
          .replaceAll('⅓', '1/3')
          .replaceAll('½', '1/2')
          .replaceAll('¼', '1/4')
          .replace(/\s+/g, ' ')
          .trim();
      }

      findM3Variants() {
        if (!this.variants.length) return {};

        const getVariantText = (variant) => {
          const parts = [
            variant.title,
            variant.public_title,
            variant.option1,
            variant.option2,
            variant.option3,
            variant.sku,
            ...(Array.isArray(variant.options) ? variant.options : []),
          ];

          return this.normalise(parts.filter(Boolean).join(' '));
        };

        const isThirdM3Variant = (variant) => {
          const text = getVariantText(variant);

          return (
            text.includes('1/3') ||
            text.includes('one third') ||
            text.includes('onethird') ||
            text.includes('0.33') ||
            text.includes('0.333') ||
            text.includes('.33') ||
            text.includes('-33') ||
            text.includes('spg-.33') ||
            text.includes('spg.33')
          );
        };

        const isFullM3Variant = (variant) => {
          const text = getVariantText(variant);

          if (isThirdM3Variant(variant)) return false;

          return (
            text.includes('1 m3') ||
            text.includes('1m3') ||
            text.includes('1 cubic') ||
            text.includes('1cubic') ||
            text.includes('volume 1') ||
            text.match(/(^|\s)1(\s|$)/)
          );
        };

        return {
          full: this.variants.find(isFullM3Variant),
          third: this.variants.find(isThirdM3Variant),
        };
      }

      getNumber(input) {
        if (!input) return 0;

        const value = parseFloat(input.value);
        return Number.isFinite(value) && value > 0 ? value : 0;
      }

      formatNumber(value, decimals = 2) {
        return Number(value)
          .toFixed(decimals)
          .replace(/\.?0+$/, '');
      }

      calculate() {
        const length = this.getNumber(this.lengthInput);
        const width = this.getNumber(this.widthInput);
        const depth = this.getNumber(this.depthInput);

        this.hideError();

        if (!length || !width || (this.mode === 'm3' && !depth)) {
          this.state = null;
          if (this.result) this.result.hidden = true;
          return;
        }

        if (this.mode === 'm3') {
          this.calculateM3(length, width, depth);
        } else {
          this.calculateM2(length, width);
        }
      }

      calculateM2(length, width) {
        const calculatedArea = length * width;
        const orderQuantity = Math.ceil(calculatedArea);

        this.state = {
          mode: 'm2',
          length,
          width,
          calculated: calculatedArea,
          orderQuantity,
        };

        if (this.resultMain) {
          this.resultMain.textContent = `Calculated area: ${this.formatNumber(calculatedArea)} m²`;
        }

        if (this.resultOrder) {
          this.resultOrder.textContent = `Recommended order quantity: ${orderQuantity} m²`;
        }

        if (this.result) {
          this.result.hidden = false;
        }

        if (this.behaviour === 'product') {
          this.attachCartHandlers();

          const quantityInput = this.findProductForm()?.querySelector('[name="quantity"]');

          if (quantityInput) {
            quantityInput.value = orderQuantity;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      calculateM3(length, width, depth) {
        const calculatedVolume = length * width * depth;
        const totalThirds = Math.ceil((calculatedVolume * 3) - 0.000001);
        const fullQuantity = Math.floor(totalThirds / 3);
        const thirdQuantity = totalThirds % 3;
        const roundedVolume = totalThirds / 3;

        this.state = {
          mode: 'm3',
          length,
          width,
          depth,
          calculated: calculatedVolume,
          roundedVolume,
          totalThirds,
          fullQuantity,
          thirdQuantity,
        };

        if (this.resultMain) {
          this.resultMain.textContent = `Calculated volume: ${this.formatNumber(calculatedVolume, 3)} m³`;
        }

        if (this.resultOrder) {
  if (this.behaviour === 'product') {
    const parts = [];

    if (fullQuantity > 0) {
      parts.push(`${fullQuantity} × 1m³`);
    }

    if (thirdQuantity > 0) {
      parts.push(`${thirdQuantity} × ⅓m³`);
    }

    this.resultOrder.textContent = `Recommended order: ${parts.join(' + ')} (${this.formatNumber(roundedVolume, 3)} m³ total)`;
    this.resultOrder.hidden = false;
  } else {
    this.resultOrder.textContent = '';
    this.resultOrder.hidden = true;
  }
}

        if (this.result) {
          this.result.hidden = false;
        }
      }

      handleButtonClick(event) {
        this.calculate();

        if (!this.state || this.behaviour !== 'product') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.addCalculatedItemsToCart();
      }

      handleProductSubmit(event) {
        this.calculate();

        if (!this.state || this.behaviour !== 'product') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.addCalculatedItemsToCart();
      }

      addCalculatedItemsToCart() {
        this.form = this.findProductForm();

        if (!this.form) {
          this.showError('The calculator could not find the product form.');
          return;
        }

        if (this.mode === 'm3') {
          this.addM3ItemsToCart();
        } else {
          this.addM2ItemToCart();
        }
      }

      getSelectedVariantId() {
        const form = this.form || this.findProductForm();
        if (!form) return null;

        const idInput = form.querySelector('[name="id"]');

        if (!idInput || !idInput.value) return null;

        return idInput.value;
      }

      buildM2Properties() {
        return {
          Length: `${this.formatNumber(this.state.length)}m`,
          Width: `${this.formatNumber(this.state.width)}m`,
          'Calculated area': `${this.formatNumber(this.state.calculated)}m²`,
          'Order quantity': `${this.state.orderQuantity}m²`,
        };
      }

      buildM3Properties() {
        return {
          Length: `${this.formatNumber(this.state.length)}m`,
          Width: `${this.formatNumber(this.state.width)}m`,
          Depth: `${this.formatNumber(this.state.depth)}m`,
          'Calculated volume': `${this.formatNumber(this.state.calculated, 3)}m³`,
          'Rounded order volume': `${this.formatNumber(this.state.roundedVolume, 3)}m³`,
        };
      }

      addM2ItemToCart() {
        const variantId = this.getSelectedVariantId();

        console.info('[Measurement calculator] M2 variant ID:', variantId);
        console.info('[Measurement calculator] M2 quantity:', this.state.orderQuantity);

        if (!variantId) {
          this.showError('Please select an available option before adding to cart.');
          return;
        }

        this.addItemsToCart([
          {
            id: Number(variantId),
            quantity: Number(this.state.orderQuantity),
            properties: this.buildM2Properties(),
          },
        ]);
      }

      addM3ItemsToCart() {
        if (!this.m3Variants.full || !this.m3Variants.third) {
          this.showError(
            'The calculator could not identify both the 1m³ and ⅓m³ variants. Please check the variant names.'
          );
          return;
        }

        const properties = this.buildM3Properties();
        const items = [];

        if (this.state.fullQuantity > 0) {
          items.push({
            id: Number(this.m3Variants.full.id),
            quantity: Number(this.state.fullQuantity),
            properties,
          });
        }

        if (this.state.thirdQuantity > 0) {
          items.push({
            id: Number(this.m3Variants.third.id),
            quantity: Number(this.state.thirdQuantity),
            properties,
          });
        }

        this.addItemsToCart(items);
      }

      getSectionsPayload() {
        const miniCart = this.getMiniCart();

        if (!miniCart || typeof miniCart.getSectionsToRender !== 'function') {
          return {};
        }

        return {
          sections: miniCart.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        };
      }

      setLoading(isLoading) {
        const submitButton = this.getSubmitButton();
        const spinner = this.form?.querySelector('.button-overlay-spinner');

        if (submitButton) {
          submitButton.classList.toggle('loading', isLoading);

          if (isLoading) {
            submitButton.setAttribute('aria-disabled', 'true');
          } else {
            submitButton.removeAttribute('aria-disabled');
          }
        }

        if (spinner) {
          spinner.classList.toggle('hidden', !isLoading);
        }
      }

      addItemsToCart(items) {
        const { cartAddUrl, cartUrl } = this.getRoutes();
        const miniCart = this.getMiniCart();

        this.hideError();
        this.setLoading(true);

        const body = {
          items,
          ...this.getSectionsPayload(),
        };

        console.info('[Measurement calculator] Add payload:', body);

        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then(async (response) => {
            const data = await response.json().catch(() => null);

            if (!response.ok || data?.status || data?.errors) {
              const message =
                data?.description ||
                data?.message ||
                data?.errors ||
                'Something went wrong. Please check the quantity available and try again.';

              throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
            }

            return data;
          })
          .then(async (response) => {
            const verifiedCart = await this.fetchCart();

            const addedVariantIds = items.map((item) => Number(item.id));
            const itemWasAdded = verifiedCart.items.some((cartItem) =>
              addedVariantIds.includes(Number(cartItem.id))
            );

            console.info('[Measurement calculator] Verified cart:', verifiedCart);

            if (!itemWasAdded) {
              throw new Error(
                'Shopify returned a success response, but the calculated item was not found in the cart.'
              );
            }

            if (
              response?.sections &&
              miniCart &&
              typeof miniCart.renderContents === 'function'
            ) {
              miniCart.renderContents(response);
            } else if (miniCart && typeof miniCart.updateSectionContents === 'function') {
              await miniCart.updateSectionContents();
            }

            if (window.themeSettings?.redirectToCart) {
              window.location.href = cartUrl;
            } else if (miniCart && typeof miniCart.open === 'function') {
              miniCart.open(this.getSubmitButton());
            }

            document.dispatchEvent(
              new CustomEvent('cart:item-add', {
                bubbles: true,
                detail: {
                  items,
                  cart: verifiedCart,
                  context: 'MeasurementCalculator',
                },
              })
            );
          })
          .catch((error) => {
            console.error('[Measurement calculator] Add to cart failed:', error);
            this.showError(error.message || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      fetchCart() {
        return fetch('/cart.js', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        }).then((response) => response.json());
      }

      showError(message) {
        if (!this.error) return;

        this.error.textContent = message;
        this.error.hidden = false;
      }

      hideError() {
        if (!this.error) return;

        this.error.textContent = '';
        this.error.hidden = true;
      }
    }
  );
}